import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { apiErrorReason } from "@/lib/http.ts";
import { whatsappPickerGroupsQueryOptions } from "@/shared/whatsappGroupQueries.ts";
import {
  hasWhatsappGroupToAdd,
  type WhatsappGroupPickerRefusalReason,
} from "@/types/whatsappGroups.ts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { WhatsappGroupPickerForm } from "./WhatsappGroupPickerForm.tsx";

type Props = { onClose: () => void };

const NOT_LIVE: WhatsappGroupPickerRefusalReason = "notLive";

/**
 * The first read lands the moment a link does, when the session can still be
 * settling on the host. This is the only time the offer is made, so a
 * `notLive` answer is retried a few times before it counts as the answer.
 * Exported for its unit test.
 */
export function retryWhileConnecting(failureCount: number, error: unknown): boolean {
  return apiErrorReason(error) === NOT_LIVE && failureCount < 3;
}

/**
 * "Add okthx to your WhatsApp groups", opened right after someone links
 * WhatsApp, in the tab that ran the link.
 *
 * Whether to open is decided once, from the first answer: okthx already in
 * every group the user runs, no admin groups, or a list that can't be read,
 * closes it without showing anything. Deciding again on a later refetch would
 * let the offer appear on its own long after the link, or vanish mid-pick.
 */
export const WhatsappGroupPickerDialog = ({ onClose }: Props) => {
  const { data, isError, isFetching } = useQuery({
    ...whatsappPickerGroupsQueryOptions(),
    // Always read fresh: a list cached from an earlier link (an unlink and
    // relink inside its stale time) would decide on groups okthx has since
    // joined. The form mounting after this reuses the read.
    refetchOnMount: "always",
    retry: retryWhileConnecting,
    retryDelay: 2_000,
  });
  const [open, setOpen] = useState(false);
  // Not while a read is in flight, retries included: the fresh answer decides.
  const decided = !isFetching && (data !== undefined || isError);
  // A failed refetch keeps the last list in `data`; only a fresh answer offers.
  const offer = !isError && data !== undefined && hasWhatsappGroupToAdd(data.groups);

  useEffect(() => {
    if (open || !decided) return;
    if (offer) setOpen(true);
    else onClose();
  }, [open, decided, offer, onClose]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-md flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Add okthx to your WhatsApp groups</DialogTitle>
          <DialogDescription>
            okthx can help in the groups it's in. These are the groups you're an admin of; okthx
            joins by invite link and stays quiet until someone talks to it.
          </DialogDescription>
        </DialogHeader>
        <WhatsappGroupPickerForm onJoined={onClose} onCancel={onClose} />
      </DialogContent>
    </Dialog>
  );
};
